import { Gem, Sparkles, Headset, Rocket } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { GlassCard } from "@/components/GlassCard";
import { FeatureCard } from "@/components/FeatureCard";
import { Reveal } from "@/components/Reveal";
import { SUPPORT_SERVER_URL } from "@/config";

const BENEFITS = [
  { icon: Gem, title: "Enhanced limits", description: "Higher caps across modules — more reaction roles, more ticket panels, more automation." },
  { icon: Rocket, title: "Priority performance", description: "Premium servers get priority processing for commands and background tasks." },
  { icon: Headset, title: "Priority support", description: "Skip the queue with a dedicated premium support channel in the support server." },
  { icon: Sparkles, title: "Early access", description: "Try new modules and features before they roll out to everyone." },
];

const PREMIUM_FAQ = [
  { q: "When will Premium be available?", a: "Premium tiers are currently being finalized. Join the support server to be notified the moment they launch." },
  { q: "How will activation work?", a: "Premium will be activated per server. Redeem and activation details will be published here and in the support server at launch." },
  { q: "Will free features stay free?", a: "Yes. Xevon's core modules — moderation, security, tickets, leveling, voice, and more — remain free." },
];

export default function Premium() {
  useSEO({
    title: "Premium",
    description: "Xevon Premium — enhanced limits, priority performance, and early access. Tiers coming soon.",
    path: "/premium",
  });

  return (
    <>
      <PageHeader
        kicker="Premium"
        title="Take Xevon further."
        description="An optional upgrade for servers that want more — more limits, more priority, more of what's next."
      />
      <section className="mx-auto w-full max-w-7xl px-6 pb-32" data-testid="premium-section">
        <Reveal>
          <GlassCard className="relative overflow-hidden p-10 text-center md:p-16" hover={false}>
            <div
              className="pointer-events-none absolute inset-0"
              aria-hidden="true"
              style={{ background: "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(255,255,255,0.07), transparent 70%)" }}
            />
            <span
              className="inline-block rounded-full border border-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-paper-100"
              data-testid="premium-coming-soon"
            >
              Coming Soon
            </span>
            <h2 className="mx-auto mt-6 max-w-2xl font-display text-3xl font-semibold tracking-tight text-paper-100 md:text-4xl">
              Premium tiers are on the way.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-paper-400 md:text-base">
              Pricing and tier details will be announced in the support server first. Activation and
              redeem instructions will be published here when Premium launches.
            </p>
            <a
              href={SUPPORT_SERVER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="premium-notify-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.04]"
            >
              Get notified in the support server
            </a>
          </GlassCard>
        </Reveal>

        <div className="mt-20">
          <Reveal>
            <h3 className="font-display text-2xl font-semibold tracking-tight text-paper-100 md:text-3xl">
              What to expect
            </h3>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.06}>
                <FeatureCard icon={b.icon} title={b.title} description={b.description} testId={`premium-benefit-${i}`} />
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-20 max-w-3xl">
          <Reveal>
            <h3 className="font-display text-2xl font-semibold tracking-tight text-paper-100 md:text-3xl">
              Premium FAQ
            </h3>
          </Reveal>
          <div className="mt-8 space-y-4">
            {PREMIUM_FAQ.map((item, i) => (
              <Reveal key={item.q} delay={i * 0.05}>
                <GlassCard className="p-6" hover={false} data-testid={`premium-faq-${i}`}>
                  <p className="font-display text-base font-semibold text-paper-100">{item.q}</p>
                  <p className="mt-2 text-sm leading-relaxed text-paper-400">{item.a}</p>
                </GlassCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
